/* CASH REPORT — THE WEEKLY REPORT (phase 2, 2026-10-08).

   Tuan's format (03. Weekly cash Report - format): the GROUP and each entity under it — Opening,
   Inflow, Outflow, Closing, Restricted, Fixed Deposit, Available, the change on last week, the
   minimum-balance flag and the report's status — every figure in SGD, each entity opening into its
   intercompany transactions and its accounts. ONE WEEK, the one the period bar picks (Max, 2026-10-13,
   reversing the year table built that morning for Tuan).
   Under it, the Top 3 spendings and payables of the entity chosen.

   NO ENTITY PANEL UNDER IT (Max, 2026-10-15: "phần này không cần hiện trong các report nhé"). An
   entity's own version — its four steps, Data Check, Fields 1-6, files and history — is read where
   it is decided, Cash Report Approval (48_CashApproval.jsx), and figures come IN through Submit Cash
   Report (46_CashImport.jsx). This screen is the report alone. Every figure on it comes from the
   server's one computation — the screen adds nothing up. */
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { AlertTriangle, ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import { API, call, fmtDay, fmtStamp, moneyIn, Num, StateChip, lastStamp, PeriodBar, TestAsBar, WaitingTag, BasePick, readBase, saveBase, toBase } from './cashUi';
import { WeeklyCharts } from './CashCharts';
import './SPHub.css';
import './ProjectApproval.css';
import './CashSetup.css';
import './CashWeekly.css';

/* ---- THE WEEK AS BALANCES — Jay Sung for the Finance team, 2026-10-21, and Tuan after the call,
   2026-10-22 ("simplify the cash dashboard to show only the current balance (instead of opening, closing,
   inflow, and outflow information)"; "no need to show intercompany transactions"; "cash by bank account
   can be shown by clicking the cash balance"; "restricted cash may need to be shown"). So the table reads
   Balance · Restricted · Fixed deposit · Available · Change, the intercompany row is gone, and a click on
   an entity's balance (or anywhere on its row) opens its bank accounts. Inflow, outflow and the transfers
   are still entered and checked on Submit — they make the balance trustworthy — they are just not what this
   page is read for.
   EVERY AMOUNT IS IN THE BASE CURRENCY THE READER PICKS (SGD / USD / KRW), worked out from SGD at the week's
   rate; an account's OWN balance stands beside it in its own currency, as the bank prints it. */
/* THE WEEK TABLE'S COLUMNS, ONE WIDTH EACH (Max, 2026-10-15: "tăng width cho Bank") — fixed layout with
   the table exactly the sum (a percentage hands the slack back out); it scrolls sideways in its own box
   with the first column held (CashWeekly.css). Entity/Account · Currency · Bank · Account No. · Type of
   account · flag · Balance in the account's currency, then Balance · Restricted · Fixed deposit · Available
   in the base, Change, Below minimum, Status. */
const WEEK_COLS = [150, 72, 130, 140, 140, 120, 210, 160, 150, 150, 160, 150, 120, 175];
/* THE ACCOUNT COLUMNS SHOW ONLY WITH AN ACCOUNT ON SCREEN (Tuan via Max, 2026-10-16). They describe an
   ACCOUNT, so on the GROUP and entity rows they were empty cells pushing the figures off the screen; they
   come back the moment any entity is opened into its accounts. Bank · Account No. · Type · flag · the
   account's own balance are WEEK_COLS 2-6. */
const ACCT_COLS = [2, 3, 4, 5, 6];
const acctBlanks = (detail) => (detail ? <><td /><td /><td /><td /><td /></> : null);
const HEAD = ['restricted', 'fd', 'available', 'change', 'belowMin', 'status'];

/* A ROW THAT OPENS ANYWHERE ON IT (Max, 2026-10-15: "khi click trên dòng đó thì sẽ expand ra chứ hiện phải
   click vào icon"). A click on a link or another button in the row (the Waiting-for-you tag) is that
   control's own, and the chevron stops its click so the row does not toggle twice. */
const rowToggle = (fn) => (ev) => { if (ev.target.closest('a, button')) return; fn(); };
const chevToggle = (fn) => (ev) => { ev.stopPropagation(); fn(); };

function EntityRows({ t, data, week, entities, open, toggle, detail, base, cv }) {
  const onScreen = week === data.period.code;
  return entities.map((e) => {
    const isOpen = open.has(e.entity);
    const ls = lastStamp(e.stamps);
    return (
      <React.Fragment key={e.entity}>
        <tr className={`cw-entrow${e.totals ? ' cw-rowtoggle' : ''}`} onClick={e.totals ? rowToggle(() => toggle(e.entity)) : undefined}>
          <td className="cw-entcell">
            <button type="button" className="cw-expand" disabled={!e.totals} onClick={chevToggle(() => toggle(e.entity))}
              aria-expanded={isOpen} title={e.totals ? t('cashw.expandWhy') : undefined}>
              {e.totals ? (isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />) : <span className="cw-nochev" />}
            </button>
            <span className="cw-entlink" title={e.name}>{e.entity}</span>
            {e.errors > 0 && <span className="cs-tab-err" title={t('cashw.errorsN', { count: e.errors })}>{e.errors}</span>}
            {onScreen && <WaitingTag t={t} data={data} entity={e.entity} />}
          </td>
          <td className="cw-ccycol">{e.totals ? base : ''}</td>{acctBlanks(detail)}
          {e.totals ? (
            <>
              <td className="num">
                {/* The balance is the thing to press (Jay Sung: "by clicking the cash balance"). */}
                <button type="button" className="cw-ballink" aria-expanded={isOpen} title={t('cashw.balanceWhy')}
                  onClick={chevToggle(() => toggle(e.entity))}><b><Num v={cv(e.totals.closing)} ccy={base} /></b></button>
              </td>
              <td className="num"><Num v={cv(e.totals.restricted)} ccy={base} /></td><td className="num"><Num v={cv(e.totals.fixed_deposit)} ccy={base} /></td>
              <td className="num"><b><Num v={cv(e.totals.available)} ccy={base} /></b></td><td className="num"><Num v={cv(e.change)} sign ccy={base} /></td>
              <td className="num">{e.totals.below_min
                ? <span className="cw-flag" title={t('cashw.belowWhy', { min: moneyIn(e.minBalance, 'SGD', 0) })}><AlertTriangle size={13} /> {t('cashw.below')}</span>
                : (e.minBalance == null ? <span className="pa-muted" title={t('cashw.noMinWhy')}>—</span> : '')}</td>
            </>
          ) : <td colSpan={6} className="pa-muted">{e.pending ? t('cashw.notReviewed') : t('cashw.notUploaded')}</td>}
          <td>
            <StateChip t={t} row={e} />
            {ls && <div className="cw-sub">{t(`cashw.by_${ls[0]}`, { by: ls[1], at: fmtStamp(ls[2]) })}</div>}
          </td>
        </tr>
        {isOpen && e.totals && e.accounts.map((a) => (
          <tr key={a.account_id} className="cw-subrow cw-acctrow">
            {/* ONE FACT A COLUMN (Max, 2026-10-15: "tách này ra thành nhiều cột chứ ko gộp lại"). */}
            <td className="cw-indent"><span className="mono">{a.account_id}</span></td>
            <td className="cw-ccycol">{a.currency}</td>
            <td>{a.bank_name || ''}</td>
            <td className="mono">{a.account_no || ''}</td>
            <td>{a.account_type || ''}</td>
            {/* Restricted the dark red, Fixed deposit the light one (Max, 2026-10-15). */}
            <td>
              {a.restricted && <span className="cw-flagtag restricted">{t('cashw.restrictedTag')}</span>}
              {a.fixed_deposit && <span className="cw-flagtag fd">{t('cashw.fdTag')}</span>}
            </td>
            {/* The balance as the bank prints it, in the account's own currency. */}
            <td className="num cw-orignum" title={t('cashw.origWhy', { ccy: a.currency })}><Num v={a.orig.closing} ccy={a.currency} /></td>
            <td className="num"><Num v={cv(a.sgd?.closing)} ccy={base} /></td>
            <td className="num">{a.restricted ? <Num v={cv(a.sgd?.restricted)} ccy={base} /> : ''}</td>
            <td className="num">{a.fixed_deposit ? <Num v={cv(a.sgd?.fixed_deposit)} ccy={base} /> : ''}</td>
            <td className="num"><Num v={cv(a.sgd?.available)} ccy={base} /></td>
            <td colSpan={3} />
          </tr>
        ))}
      </React.Fragment>
    );
  });
}

/* ONE WEEK, THE ONE ON SCREEN — Max, 2026-10-13: "Weekly cash report thì ko cần hiện tất cả các tuần nhé".
   Its GROUP row, then every entity, each opening into its bank accounts. */
function WeekTable({ t, data, folds, base, cv }) {
  const g = data.group;
  const reported = data.entities.filter((e) => e.totals).length;
  /* ONE PAIR OF BUTTONS FOR BOTH TABLES (Max, 2026-10-15): Expand all opens every entity into its accounts
     here and its Top 3 below; Collapse all folds both. Each is dead when it would change nothing. */
  const ents = data.entities.filter((e) => e.totals).map((e) => e.entity);
  const { weekOpen, setWeekOpen, topShut, setTopShut } = folds;
  const allOpen = ents.every((c) => weekOpen.has(c)) && ents.every((c) => !topShut.has(c));
  const allShut = ents.every((c) => !weekOpen.has(c)) && ents.every((c) => topShut.has(c));
  const toggleWeek = (code) => setWeekOpen((x) => { const n = new Set(x); if (n.has(code)) n.delete(code); else n.add(code); return n; });
  const detail = ents.some((c) => weekOpen.has(c));
  const cols = detail ? WEEK_COLS : WEEK_COLS.filter((_, i) => !ACCT_COLS.includes(i));
  return (
    <div className="pa-section cw-group" data-tour="cr-week">
      <div className="cw-tophead">
        <h3>{t('cashw.weekTitle', { code: data.period.code, end: fmtDay(data.period.end) })}</h3>
        <div className="cw-tophead-picks">
          <button type="button" className="pa-btn cw-expandall" disabled={!ents.length || allOpen}
            onClick={() => { setWeekOpen(new Set(ents)); setTopShut(new Set()); }}>
            <ChevronDown size={14} /> {t('cashw.expandAll')}
          </button>
          <button type="button" className="pa-btn cw-collapseall" disabled={!ents.length || allShut}
            onClick={() => { setWeekOpen(new Set()); setTopShut(new Set(ents)); }}>
            <ChevronRight size={14} /> {t('cashw.collapseAll')}
          </button>
        </div>
      </div>
      <div className="cs-table-wrap cw-yearwrap">
        <table className={`pa-table cw-table cw-yeartable cw-weekfixed${detail ? ' cw-detail' : ''}`} style={{ width: cols.reduce((a, b) => a + b, 0) }}>
          <colgroup>{cols.map((w, i) => <col key={i} style={{ width: w }} />)}</colgroup>
          <thead><tr>
            <th>{t('cashw.h_weekEntity')}</th><th>{t('cashw.h_ccy')}</th>
            {detail && <><th>{t('cashw.h_bank')}</th><th>{t('cashw.h_acctNo')}</th><th>{t('cashw.h_acctType')}</th><th>{t('cashw.h_acctFlag')}</th>
              <th className="num">{t('cashw.h_balanceOrig')}</th></>}
            <th className="num">{t('cashw.h_balance', { ccy: base })}</th>
            {HEAD.map((h) => <th key={h} className={h === 'status' ? '' : 'num'}>{t(`cashw.h_${h}`)}</th>)}
          </tr></thead>
          <tbody>
            <tr className="cw-grouprow cw-weekrow onscreen" data-week={data.period.code}>
              <td><span className="cw-tag cw-grouptag">GROUP</span></td>
              <td className="cw-ccycol">{g ? base : ''}</td>{acctBlanks(detail)}
              {g ? (
                <>
                  <td className="num"><b><Num v={cv(g.closing)} ccy={base} /></b></td>
                  <td className="num"><Num v={cv(g.restricted)} ccy={base} /></td><td className="num"><Num v={cv(g.fixed_deposit)} ccy={base} /></td>
                  <td className="num"><b><Num v={cv(g.available)} ccy={base} /></b></td>
                  <td className="num" title={g.change != null ? t('cashw.changeOver', { count: g.changeOver }) : undefined}><Num v={cv(g.change)} sign ccy={base} /></td>
                  <td /><td className="cw-sub">{t('cashw.groupCount', { n: reported, of: data.entities.length })}</td>
                </>
              ) : <td colSpan={7} className="pa-muted cw-weeknone">{t('cashw.weekNothing')}</td>}
            </tr>
            {data.entities.length
              ? <EntityRows t={t} data={data} week={data.period.code} entities={data.entities} open={weekOpen} toggle={toggleWeek} detail={detail} base={base} cv={cv} />
              : <tr className="cw-subrow"><td colSpan={cols.length} className="cw-indent pa-muted">{t('cashw.nothingYet')}</td></tr>}
          </tbody>
        </table>
      </div>
      <p className="cs-hint">{t('cashw.balanceNote')}</p>
    </div>
  );
}

/* TOP 3 OF EVERY ENTITY — Max, 2026-10-15: "chỗ này hiện ko phù hợp cho người xem khi phải click từng
   cái entity á … show hết top 3 của tất cả entity và còn thêm nút expand và collapse all cho cả 2 bảng".
   A dropdown of one entity at a time made ten picks of reading a week. Both tables now list every
   entity with a version this week, each a group that folds, its three amounts' total on the group's
   own line so a folded group still says something. The fold is ONE state per entity shared by the
   two tables, so an entity's spendings and payables open and close together and stay side by side;
   Expand all / Collapse all act on both. Everything starts open — the point is to see them all.
   AND THE ENTITIES THAT HAVE NOT REPORTED ARE LISTED TOO (Max, same day: "những entity mà chưa submit cũng
   hiện luôn nhé chứ ko hiện là ko biết entity nào bị sót"), as the week table above lists them: a line
   that says "Not uploaded yet" and nothing to fold. The card's own "Top 3" heading went the same hour —
   the two table titles already say it. */
function TopThree({ t, data, folds, base, cv }) {
  const all = data.entities;
  const shut = folds.topShut;
  const toggle = (code) => folds.setTopShut((x) => { const n = new Set(x); if (n.has(code)) n.delete(code); else n.add(code); return n; });
  const sumOf = (e, kind) => (e.tops || []).filter((x) => x.kind === kind && x.amount_sgd != null)
    .reduce((acc, x) => (acc ?? 0) + Number(x.amount_sgd), null);
  const block = (kind) => (
    <div className="cw-top">
      <h4>{t(`cashw.top_${kind}`)}</h4>
      <table className="pa-table cw-table cw-toptable" data-kind={kind}>
        <colgroup><col style={{ width: 50 }} /><col style={{ width: '32%' }} /><col /><col style={{ width: 130 }} /></colgroup>
        <thead><tr><th>{t('cashw.h_no')}</th><th>{t('cashw.h_category')}</th><th>{t('cashw.h_memo')}</th><th className="num">{t('cashw.h_amountIn', { ccy: base })}</th></tr></thead>
        <tbody>
          {all.map((e) => {
            if (!e.totals) {
              return (
                <tr key={e.entity} className="cw-topent cw-topnone" data-entity={e.entity}>
                  <td colSpan={3}>
                    <span className="cw-nochev" />
                    <b>{e.entity}</b> <span className="pa-muted">· {e.name}</span>
                    <span className="pa-muted cw-topnone-why"> — {e.pending ? t('cashw.notReviewed') : t('cashw.notUploaded')}</span>
                  </td>
                  <td />
                </tr>
              );
            }
            const open = !shut.has(e.entity);
            return (
              <React.Fragment key={e.entity}>
                <tr className="cw-topent cw-rowtoggle" data-entity={e.entity} onClick={rowToggle(() => toggle(e.entity))}>
                  <td colSpan={3}>
                    <button type="button" className="cw-expand" onClick={chevToggle(() => toggle(e.entity))} aria-expanded={open}
                      title={open ? t('cashw.collapseAll') : t('cashw.expandAll')}>
                      {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    </button>
                    <b>{e.entity}</b> <span className="pa-muted">· {e.name}</span>
                  </td>
                  <td className="num"><b><Num v={cv(sumOf(e, kind))} ccy={base} /></b></td>
                </tr>
                {open && [1, 2, 3].map((n) => {
                  const r = (e.tops || []).find((x) => x.kind === kind && x.rank_no === n);
                  return (
                    <tr key={n} className="cw-subrow">
                      <td className="cw-indent">{n}</td>
                      <td>{r?.category_code ? <>{r.category || r.category_code} <span className="pa-muted mono">{r.category_code}</span></> : ''}</td>
                      <td className="cs-wrap">{r?.memo || ''}</td>
                      <td className="num">{r ? <Num v={cv(r.amount_sgd)} ccy={base} /> : ''}</td>
                    </tr>
                  );
                })}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
  return (
    <div className="pa-section" data-tour="cr-top3">
      {/* Its Expand all / Collapse all moved up to the week table, and drive both (2026-10-15). */}
      {all.length
        ? <div className="cw-tops">{block('spend')}{block('payable')}</div>
        : <div className="pa-empty">{t('cashw.noTops')}</div>}
    </div>
  );
}

/* ---- the screen ------------------------------------------------------------------------------ */
export default function CashWeekly() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  // `period` is the screen's own word; `week` is what links written before it said.
  const period = params.get('period') || params.get('week') || '';
  /* The folds of the week table (entities opened into their accounts — start closed) and of Top 3
     (entities folded — start open), held here so one pair of buttons can drive both. */
  const [weekOpen, setWeekOpen] = useState(() => new Set());
  const [topShut, setTopShut] = useState(() => new Set());
  const folds = { weekOpen, setWeekOpen, topShut, setTopShut };
  const [base, setBaseState] = useState(readBase);
  const setBase = (b) => { setBaseState(b); saveBase(b); };

  const load = useCallback(async () => {
    try {
      const j = await call(`${API}/view/weekly${period ? `?period=${encodeURIComponent(period)}` : ''}`);
      setData(j); setError('');
    } catch (e) { setError(e.message); }
  }, [period]);
  useEffect(() => { load(); }, [load]);

  const setQuery = (next) => {
    const p = new URLSearchParams(params);
    p.delete('week');
    Object.entries(next).forEach(([k, v]) => { if (v) p.set(k, v); else p.delete(k); });
    setParams(p, { replace: true });
  };

  if (error) {
    return <div className="pa-banner err">{error}</div>;
  }
  if (!data) {
    return <div className="pa-empty"><Loader2 size={16} className="pa-spin" /> {t('cash.loading')}</div>;
  }
  const w = data.period;
  const rateCount = Object.keys(data.rates || {}).length;
  /* A base with no rate for the week falls back to SGD and says so, rather than printing blanks. */
  const baseOk = base === 'SGD' || Number(data.rates?.[base]) > 0;
  const shown = baseOk ? base : 'SGD';
  const cv = (sgd) => toBase(sgd, data.rates, shown);
  return (
    <>
        <TestAsBar t={t} me={data.me} />

        <PeriodBar t={t} periods={data.periods} period={w} onPick={(code) => setQuery({ period: code })}
          trail={<BasePick t={t} value={base} onChange={setBase} />}>
          {/* No "Open for reports" and no rate count (Max, 2026-10-15: "bỏ"): the dropdown already marks a
              closed week, and the rates come from NetSuite by themselves. Only a week with NO rate says so —
              its reports cannot be sent until there is one. */}
          {t('cashw.weekLine', { end: fmtDay(w.end), due: fmtDay(w.deadline) })}
          {!rateCount && <div className="cs-boot">{t('cashw.noRates')}</div>}
          {rateCount > 0 && !baseOk && <div className="cs-boot">{t('cashw.noBaseRate', { ccy: base })}</div>}
          {baseOk && shown !== 'SGD' && <div className="cw-sub">{t('cashw.baseRate', { ccy: shown, rate: Number(data.rates[shown]).toPrecision(6).replace(/\.?0+$/, '') })}</div>}
        </PeriodBar>

        <WeeklyCharts t={t} data={data} base={shown} />
        <WeekTable t={t} data={data} folds={folds} base={shown} cv={cv} />
        <TopThree t={t} data={data} folds={folds} base={shown} cv={cv} />
    </>
  );
}
